import { getConfig } from "@/lib/config";
import { useAuthStore } from "@/stores/auth-store";
import { createDiagnostics, type DiagnosticsLogger } from "./logger";
import type { DiagnosticInput } from "./contract";
import { redactRuntimeText } from "./redact";

export { CODES } from "./contract";
export type { DiagnosticInput, DiagnosticCategory, DiagnosticLevel } from "./contract";

// Runtime kill switch: the `spaDiagnostics` feature flag from /config.js.
// Deploy-dark by default. Never throws.
function deliveryEnabled(): boolean {
  try {
    return getConfig().featureFlags?.spaDiagnostics === true;
  } catch {
    return false;
  }
}

function buildVersion(): string | undefined {
  try {
    return getConfig().buildVersion;
  } catch {
    return undefined;
  }
}

// Dev/test-only, rate-limited console fallback. Production stays silent so a
// delivery failure never reaches the console. Emits SAFE operational metadata
// only — never event payloads, stacks, headers, or response bodies.
let lastFallbackAt = 0;
function devFallback(info: { phase: string; status?: number; dropped: number }): void {
  if (!import.meta.env.DEV) return;
  const t = Date.now();
  if (t - lastFallbackAt < 5000) return;
  lastFallbackAt = t;
  console.warn("[diagnostics] delivery degraded", info);
}

let singleton: DiagnosticsLogger | null = null;
function instance(): DiagnosticsLogger {
  if (!singleton) {
    singleton = createDiagnostics({
      isEnabled: deliveryEnabled,
      // Lazy import breaks a module cycle (auth/me → api-request → diagnostics).
      refreshAuth: () => import("@/auth/me").then((m) => m.bootstrapAuth()),
      authStore: useAuthStore,
      buildVersionProvider: buildVersion,
      devFallback,
    });
  }
  return singleton;
}

// Public, non-throwing logging entry point for producers, wired into the
// runtime/Apollo/HTTP/WS/application seams.
export function logDiagnostic(input: DiagnosticInput): void {
  instance().log(input);
}

// Best-effort keepalive flush (the runtime installer also wires this to the
// page-lifecycle events).
export function flushDiagnostics(): void {
  instance().flush();
}

// Normalize an unknown thrown value into the bounded runtime detail shape.
// Only error name/message/stack — never arbitrary enumerable properties.
export function normalizeError(err: unknown): {
  errorName: string;
  message?: string;
  stack?: string;
} {
  if (err instanceof Error) {
    return {
      errorName: redactRuntimeText(err.name || "Error", 200) ?? "Error",
      message: redactRuntimeText(err.message, 500),
      stack: redactRuntimeText(err.stack, 4000, true),
    };
  }
  return { errorName: "Error", message: redactRuntimeText(err, 500) };
}
